/**
 * The coverage vocabulary, in ONE place — owner ruling 2, "Deploy REQUIRES every
 * test point passed".
 *
 * ===========================================================================
 * WHY THIS IS A MODULE AND NOT TWO CONSTANTS IN TWO PANELS
 * ===========================================================================
 * Two screens answer for coverage and they must not say different things about the
 * same fact. `LifecyclePanels`' testing grid badges each point with WHY it is
 * outstanding; `SignoffPanel` names the outstanding numbers before a deploy. A copy
 * of {@link GAP_LABEL} in each is how one comes to read "Never tested" while the
 * other reads "Not tested yet" about the identical row, and the operator learns to
 * trust neither.
 *
 * The API states the same doctrine one layer down for the RULE itself
 * (`lifecycle/test-point-coverage.ts` — "one tested rule, one place"); this is the
 * same rule applied to the WORDS.
 *
 * ===========================================================================
 * THERE IS NO RULE IN THIS FILE, AND THAT IS DELIBERATE
 * ===========================================================================
 * Nothing here decides whether a point is covered. `coverage.covered`,
 * `coverage.points[].gap` and `coverage.outstanding` all arrive DECIDED from the
 * server, computed by the same function the deploy gate will call. A web-side
 * re-derivation would be a second implementation of a rule that must have exactly
 * one — the failure where a screen says "ready" and the server refuses.
 * {@link groupOutstanding} only re-ARRANGES a decided answer for reading.
 */

import type { CoverageGap, TestPointCoverageDto } from './api';

/**
 * WHY a point is not accounted for, in the words an operator can act on.
 *
 * Three causes rather than a count, because they call for three different actions:
 * test it, re-test it against the wording that stands, or fix the work first. A
 * screen reading "3 outstanding" tells nobody what to do about any of them.
 *
 * `Record<CoverageGap, string>` — exhaustive on purpose. Adding a cause to the API's
 * union breaks this build until somebody writes its label, which is the point: a gap
 * rendered as a raw enum value is one nobody can act on.
 */
export const GAP_LABEL: Readonly<Record<CoverageGap, string>> = {
  never_tested: 'Never tested',
  text_changed: 'Wording changed since it was tested',
  failed: 'Failed',
};

/**
 * The fixed reading order. Not the order the server sends, and not alphabetical:
 * it runs from "nothing was done" to "something was done and it failed", which is
 * roughly the order the work has to be picked up in.
 */
const GAP_ORDER: readonly CoverageGap[] = ['never_tested', 'text_changed', 'failed'];

export interface OutstandingGroup {
  gap: CoverageGap;
  label: string;
  /** The point NUMBERS, ascending. Never a count — see {@link GAP_LABEL}. */
  point_nos: number[];
}

/**
 * `coverage.outstanding` regrouped for reading: one row per CAUSE, naming the point
 * numbers under it.
 *
 * ⚠ AN EMPTY RESULT IS AMBIGUOUS ON ITS OWN AND CALLERS MUST NOT BRANCH ON IT. It
 * means EITHER "fully covered" OR "this CRF has no test points at all" — owner fork
 * 3, which says such a CRF may not deploy and which is, measured, the state of every
 * CRF at or past testing on this database. The two are told apart by
 * `coverage.points.length`, exactly as {@link TestPointCoverageDto} says. This
 * function takes the whole coverage node rather than the `outstanding` array alone
 * so that a caller cannot reach for the array and lose that distinction on the way.
 */
export function groupOutstanding(coverage: TestPointCoverageDto): OutstandingGroup[] {
  const groups: OutstandingGroup[] = [];
  for (const gap of GAP_ORDER) {
    const point_nos = coverage.outstanding
      .filter((row) => row.gap === gap)
      .map((row) => row.point_no)
      .sort((a, b) => a - b);
    if (point_nos.length > 0) groups.push({ gap, label: GAP_LABEL[gap], point_nos });
  }
  return groups;
}

/**
 * True when this CRF carries no test points at all — owner fork 3.
 *
 * A named predicate rather than `points.length === 0` written at each site, because
 * the thing it distinguishes is subtle enough to get wrong once: an uncovered CRF
 * with nothing outstanding is NOT a contradiction, it is this state, and the
 * sentence an operator needs for it ("add the requirements") is nothing like the one
 * for a genuinely outstanding point ("record the verdict").
 */
export function hasNoTestPoints(coverage: TestPointCoverageDto): boolean {
  return coverage.points.length === 0;
}

/**
 * The grid in `point_no` order — the order a tester reads a numbered list in.
 *
 * The API already sends it ordered (`ORDER BY point_no ASC`), so this changes no
 * value TODAY. It is here because this array is BOTH what gets rendered and what the
 * request body is built from, and the body pairs each `point_no` with the
 * `description` beside it. Depending on a server `ORDER BY` for the row a tester's
 * click lands on is a dependency nobody would notice losing.
 */
export function sortedGrid(coverage: TestPointCoverageDto): TestPointCoverageDto['points'] {
  return [...coverage.points].sort((a, b) => a.point_no - b.point_no);
}
